import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import { cn } from "@/utils/cn";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import ProprietaireForm from "./components/ProprietaireForm";
import { useProprietaires } from "./ProprietairesProvider";
import { PROPRIETAIRE_STATUT_LABELS, proprietaireNomComplet, type Proprietaire } from "./types";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

const statutFilterOptions = [
  { value: "", label: "Tous les statuts" },
  { value: "actif", label: "Actifs" },
  { value: "inactif", label: "Inactifs" },
];

export default function ProprietairesPage() {
  const { proprietaires, removeProprietaire } = useProprietaires();
  const { immeubles, immeublesByProprietaire, getSummary } = useAgenceData();
  const { can } = useEmployes();

  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<Proprietaire | null>(null);
  const [toDelete, setToDelete] = useState<Proprietaire | null>(null);

  const hasProprietaires = proprietaires.length > 0;

  const stats = useMemo(
    () => ({
      total: proprietaires.length,
      actifs: proprietaires.filter((item) => item.statut === "actif").length,
      inactifs: proprietaires.filter((item) => item.statut === "inactif").length,
    }),
    [proprietaires]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = proprietaires.filter((proprietaire) => {
      const matchesStatut = !statutFilter || proprietaire.statut === statutFilter;
      const matchesSearch =
        !term ||
        [
          proprietaire.prenom,
          proprietaire.nom,
          proprietaire.telephone,
          proprietaire.whatsapp,
          proprietaire.email,
          proprietaire.adresse,
          proprietaire.notes,
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesStatut && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "nom") return proprietaireNomComplet(a).localeCompare(proprietaireNomComplet(b), "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [proprietaires, search, statutFilter, sort]);

  const isFiltering = Boolean(search.trim()) || Boolean(statutFilter);

  const handleAdd = () => setFormOpen(true);

  const resetFilters = () => {
    setSearch("");
    setStatutFilter("");
  };

  const exportColumns: ExportColumn<Proprietaire>[] = [
    { header: "Nom complet", value: (proprietaire) => proprietaireNomComplet(proprietaire) },
    { header: "Téléphone", value: (proprietaire) => proprietaire.telephone },
    { header: "WhatsApp", value: (proprietaire) => proprietaire.whatsapp },
    { header: "Email", value: (proprietaire) => proprietaire.email },
    { header: "Adresse", value: (proprietaire) => proprietaire.adresse },
    { header: "Statut", value: (proprietaire) => PROPRIETAIRE_STATUT_LABELS[proprietaire.statut] },
    { header: "Créé le", value: (proprietaire) => formatDate(proprietaire.createdAt) },
  ];

  const columns: DataTableColumn<Proprietaire>[] = [
    {
      key: "identite",
      header: "Propriétaire",
      className: "min-w-[220px]",
      render: (proprietaire) => {
        const actif = proprietaire.statut === "actif";
        const initials = `${proprietaire.prenom.charAt(0)}${proprietaire.nom.charAt(0)}`.toUpperCase() || "?";
        return (
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-label text-xs font-semibold",
                actif ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500"
              )}
            >
              {initials}
            </span>
            <span className="truncate font-label font-medium text-foreground-950">
              {proprietaireNomComplet(proprietaire)}
            </span>
          </div>
        );
      },
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[150px]",
      render: (proprietaire) => <span className="text-foreground-700">{proprietaire.telephone || "Non renseigné"}</span>,
    },
    {
      key: "whatsapp",
      header: "WhatsApp",
      className: "min-w-[140px]",
      render: (proprietaire) => (
        <span className="text-foreground-700">{proprietaire.whatsapp || "Non renseigné"}</span>
      ),
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[200px]",
      render: (proprietaire) => <span className="text-foreground-700">{proprietaire.email || "Non renseigné"}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (proprietaire) => (
        <Badge
          tone={proprietaire.statut === "actif" ? "secondary" : "neutral"}
          icon={proprietaire.statut === "actif" ? "ri-checkbox-circle-line" : "ri-pause-circle-line"}
        >
          {PROPRIETAIRE_STATUT_LABELS[proprietaire.statut]}
        </Badge>
      ),
    },
    {
      key: "immeubles",
      header: "Immeubles",
      align: "center",
      render: (proprietaire) => (
        <span className="font-label font-medium text-foreground-950">{getSummary(proprietaire.id).counts.immeubles}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[200px]",
      render: (proprietaire) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/agence/proprietaires/${proprietaire.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir la fiche
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions
            onEdit={can("proprietaires.modifier") ? () => setEditing(proprietaire) : undefined}
            onDelete={can("proprietaires.supprimer") ? () => setToDelete(proprietaire) : undefined}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Propriétaires</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Répertoirez les propriétaires dont votre agence gère le patrimoine. Chaque immeuble que vous créez est
            ensuite rattaché à un propriétaire, ce qui permet de suivre ses biens, locataires, contrats et finances.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-proprietaires" title="Propriétaires" />
          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter un propriétaire
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-user-star-line" label="Propriétaires" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-pause-circle-line" label="Inactifs" value={String(stats.inactifs)} tone="neutral" />
        <StatCard
          icon="ri-building-2-line"
          label="Immeubles rattachés"
          value={String(immeubles.length)}
          tone="accent"
        />
      </div>

      {hasProprietaires && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="prop-search" className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input
                  id="prop-search"
                  icon="ri-search-line"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom, téléphone, email..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="prop-statut-filter" className="text-xs font-label font-medium text-foreground-700">
                  Statut
                </label>
                <Select
                  id="prop-statut-filter"
                  value={statutFilter}
                  onChange={(event) => setStatutFilter(event.target.value)}
                  options={statutFilterOptions}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor="prop-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="prop-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} propriétaire{filtered.length > 1 ? "s" : ""} affiché
              {filtered.length > 1 ? "s" : ""}
              {hasProprietaires ? ` sur ${proprietaires.length}` : ""}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {!hasProprietaires && (
        <EmptyState
          icon="ri-user-star-line"
          title="Aucun propriétaire pour le moment"
          description="Ajoutez votre premier propriétaire pour commencer. Vous pourrez ensuite lui rattacher ses immeubles et suivre son patrimoine depuis sa fiche."
          action={
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un propriétaire
            </button>
          }
        />
      )}

      {hasProprietaires && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun propriétaire ne correspond à votre recherche ou au filtre sélectionné. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(proprietaire) => proprietaire.id}
          minWidthClass="min-w-[1040px]"
          caption="Propriétaires"
        />
      )}

      <ProprietaireForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeProprietaire(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer le propriétaire"
        description={
          toDelete ? `Voulez-vous vraiment supprimer « ${proprietaireNomComplet(toDelete)} » ? Cette action est définitive.` : ""
        }
        blockedMessage={
          toDelete && immeublesByProprietaire(toDelete.id).length > 0
            ? `Ce propriétaire possède ${immeublesByProprietaire(toDelete.id).length} immeuble(s) rattaché(s). Supprimez ou réattribuez d'abord ses immeubles.`
            : undefined
        }
      />
    </div>
  );
}